import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useTranslation, parseApiError, useSettings } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, FormInput, X, Loader2, GripVertical } from 'lucide-react';
import Pagination from '../components/Pagination';

const FIELD_TYPES = [
  { value: 'Text', label: 'Text Input' },
  { value: 'Number', label: 'Number Input' },
  { value: 'Currency', label: 'Currency Input' },
  { value: 'Dropdown', label: 'Dropdown / Select' },
  { value: 'Date', label: 'Date Picker' },
  { value: 'Checkbox', label: 'Checkbox' },
  { value: 'File', label: 'File Upload' },
];

const FormConfigPage = () => {
  const t = useTranslation();
  const { settings } = useSettings();
  
  const [configs, setConfigs] = useState([]);
  const [masterdata, setMasterdata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', fields: [] });
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [page]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configsRes, masterdataRes] = await Promise.all([
        axios.get(`${API}/form-configs`, { params: { page, page_size: 10 } }),
        axios.get(`${API}/masterdata`, { params: { page_size: 100 } })
      ]);
      setConfigs(configsRes.data.items);
      setTotalPages(configsRes.data.total_pages);
      setMasterdata(masterdataRes.data.items);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingId(null);
    setFormData({ 
      name: '', 
      fields: [{ name: '', label: '', field_type: 'Text', required: true, options: null, masterdata_id: null }] 
    });
    setDialogOpen(true);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({ name: item.name, fields: item.fields || [] });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (formData.fields.length === 0) {
      toast.error('At least one field is required');
      return;
    }

    // Validate fields
    for (const field of formData.fields) {
      if (!field.name || !field.label) {
        toast.error('All fields must have a name and label');
        return;
      }
    }

    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/form-configs/${editingId}`, formData);
        toast.success('Form config updated');
      } else {
        await axios.post(`${API}/form-configs`, formData);
        toast.success('Form config created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error) || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/form-configs/${deleteId}`);
      toast.success('Form config deleted');
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error) || 'Failed to delete');
    } finally {
      setDeleteId(null);
    }
  };

  const addField = () => {
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, { name: '', label: '', field_type: 'Text', required: true, options: null, masterdata_id: null }]
    }));
  };

  const updateField = (index, key, value) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((f, i) => {
        if (i !== index) return f;
        const updated = { ...f, [key]: value };
        // Clear masterdata_id if field type is not Dropdown
        if (key === 'field_type' && value !== 'Dropdown') {
          updated.masterdata_id = null;
        }
        return updated;
      })
    }));
  };

  const removeField = (index) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="form-config-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 font-['Outfit'] tracking-tight">
            {t('formConfig')}
          </h1>
          <p className="text-gray-500 mt-1">Configure document form templates</p>
        </div>
        <Button 
          onClick={handleCreate}
          className="gap-2"
          data-testid="create-form-config-btn"
        >
          <Plus size={18} />
          {t('addNew')}
        </Button>
      </div>

      {/* Table */}
      <Card className="border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : configs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FormInput className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">{t('noResults')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold text-gray-600">{t('name')}</TableHead>
                  <TableHead className="font-semibold text-gray-600">{t('fields')}</TableHead>
                  <TableHead className="font-semibold text-gray-600">{t('createdAt')}</TableHead>
                  <TableHead className="text-right font-semibold text-gray-600">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {configs.map((item) => (
                  <TableRow key={item.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>{item.fields?.length || 0} fields</TableCell>
                    <TableCell>{new Date(item.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(item)}
                          data-testid={`edit-form-config-${item.id}`}
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteId(item.id)}
                          data-testid={`delete-form-config-${item.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              {editingId ? 'Edit Form Config' : 'Create Form Config'}
            </DialogTitle>
            <DialogDescription>
              Configure the fields for this form template
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label>{t('name')}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Default Document Form"
                data-testid="form-config-name-input"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base">{t('fields')}</Label>
                <Button type="button" size="sm" variant="outline" onClick={addField} data-testid="add-form-field-btn">
                  <Plus size={14} className="mr-1" />
                  Add Field
                </Button>
              </div>

              <div className="space-y-3">
                {formData.fields.map((field, idx) => (
                  <div 
                    key={idx} 
                    className="p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-gray-400">
                        <GripVertical size={16} />
                        <span className="text-sm font-medium">Field {idx + 1}</span>
                      </div>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="text-gray-400 hover:text-red-600"
                        onClick={() => removeField(idx)}
                        data-testid={`remove-form-field-${idx}`}
                      >
                        <X size={16} />
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Field Name (key)</Label>
                        <Input
                          value={field.name}
                          onChange={(e) => updateField(idx, 'name', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                          placeholder="field_name"
                          className="font-mono text-sm"
                          data-testid={`form-field-name-${idx}`}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Label</Label>
                        <Input
                          value={field.label}
                          onChange={(e) => updateField(idx, 'label', e.target.value)}
                          placeholder="Display Label"
                          data-testid={`form-field-label-${idx}`}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Field Type</Label>
                        <Select 
                          value={field.field_type} 
                          onValueChange={(v) => updateField(idx, 'field_type', v)}
                        >
                          <SelectTrigger data-testid={`form-field-type-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {FIELD_TYPES.map(type => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {field.field_type === 'Dropdown' && (
                        <div className="space-y-1">
                          <Label className="text-xs text-gray-500">Linked Masterdata</Label>
                          <Select 
                            value={field.masterdata_id || ''} 
                            onValueChange={(v) => updateField(idx, 'masterdata_id', v)}
                          >
                            <SelectTrigger data-testid={`form-field-masterdata-${idx}`}>
                              <SelectValue placeholder="Select masterdata" />
                            </SelectTrigger>
                            <SelectContent>
                              {masterdata.map(md => (
                                <SelectItem key={md.id} value={md.id}>
                                  {md.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`required-${idx}`}
                        checked={field.required}
                        onCheckedChange={(checked) => updateField(idx, 'required', checked)}
                        data-testid={`form-field-required-${idx}`}
                      />
                      <label htmlFor={`required-${idx}`} className="text-sm text-gray-600">
                        {t('required')}
                      </label>
                    </div>
                  </div>
                ))}

                {formData.fields.length === 0 && (
                  <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg">
                    <p className="text-gray-500">No fields added yet</p>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={addField}
                    >
                      <Plus size={14} className="mr-1" />
                      Add first field
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              data-testid="save-form-config-btn"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this form configuration.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FormConfigPage;
